import { Directive, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormHelperService } from '../services/form-helper.service';
import { Subject } from 'rxjs';
import {
  AbstractControl,
  FormArray,
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { DefaultFormValue } from '../interfaces/default-form-value.interface';
import { ErrorMessagesServiceBase } from '../interfaces/error-messages.interface';

/**
 * Class with utilities for the forms creation.
 * Extend a component with this class and configure FormHelperService in order to use it.
 */
@Directive({
  selector: '[rxApplicationsFormElement]',
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class FormElement implements OnInit, OnDestroy {
  FormGroup: UntypedFormGroup | null = null;
  FormArray: UntypedFormArray | null = null;
  //FormSection: typeof FormTemplatesEnum = FormTemplatesEnum;

  protected destroyed$: Subject<void> = new Subject<void>();
  protected rootFormGroup: FormGroupDirective;
  protected bookingFormHelper: FormHelperService;
  private errorMessagesService: ErrorMessagesServiceBase;
  private removeCalled = false;

  /**
   * Default value to be used in the form creation
   */
  @Input() defaultValue!: DefaultFormValue;

  /**
   * The form will be added to the parent with this name
   */
  @Input() formSectionName;

  /**
   * When used in formarray, receiving element's index
   */
  @Input() index;

  /**
   * Form provided by the parent
   */
  form!: UntypedFormGroup;

  constructor() {
    this.rootFormGroup = inject(FormGroupDirective);
    this.bookingFormHelper = inject(FormHelperService);
    this.errorMessagesService = inject(ErrorMessagesServiceBase);
  }

  ngOnInit(): void {
    this.form = this.rootFormGroup.control;
  }

  /**
   * Adds the reactive form to the provided form instance.
   * Can be added calling either with an AbstractControl or calling the configured formHelper and retrieve the required form.
   * @param form form to be added
   * @protected
   */
  protected add(form: string | AbstractControl): void {
    this.form?.addControl(
      this.formSectionName,
      form instanceof AbstractControl ? form : this.bookingFormHelper.getTemplate(form, this.defaultValue)
    );
  }

  /**
   * Patches the value of the form that was added
   * @param value value to be set
   * @protected
   */
  protected patch(value: any): void {
    const form = this.form.get(this.formSectionName) || this.form;
    form?.patchValue(value);
  }

  /**
   * If working with form arrays, retrieves the formArray (formField) and pushes the template to it
   * @param form formGroup or form name to be added
   * @param formField the name of the form's field where the form has to be added
   * @protected
   */
  protected push(form: string | AbstractControl, formField = this.formSectionName): void {
    (this.form.get(formField) as UntypedFormArray).push(
      form instanceof AbstractControl ? form : this.bookingFormHelper.getTemplate(form, this.defaultValue)
    );
  }

  protected addOrPush(form: string | AbstractControl, formField = this.formSectionName, value?: any): void {
    const formOrControl = this.form.get(formField) as UntypedFormArray;
    if (!formOrControl) {
      this.add(form);
    } else {
      formOrControl.push(form instanceof AbstractControl ? form : this.bookingFormHelper.getTemplate(form, value));
    }
  }

  /**
   * Disables all the controls provided by string array
   * @param controls
   * @protected
   */
  protected disableControls(controls: string[]): void {
    controls.forEach((control: string) => {
      this.getControl(control)?.disable();
    });
  }

  protected shift(formField = this.formSectionName): void {
    const array: UntypedFormArray = this.form.get(formField) as UntypedFormArray;
    array.removeAt(array.length - 1);
  }

  protected clearFormArray(formField = this.formSectionName): void {
    const array: UntypedFormArray = this.form.get(formField) as UntypedFormArray;

    while (array.length !== 0) {
      array.removeAt(0);
    }
  }

  getControl(formField: string): UntypedFormControl | null {
    return this.findFormControl(formField) as UntypedFormControl;
  }

  getFormGroup(formField: string): UntypedFormGroup | null {
    return this.findFormControl(formField) as UntypedFormGroup;
  }

  hasError(fieldIdentifier: string, customMessage?: string): string | null | unknown | any {
    const formField: AbstractControl | null = this.findFormControl(fieldIdentifier);
    return formField && formField.invalid && formField.touched
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.errorMessagesService.getErrorMessage(formField.errors!, fieldIdentifier, customMessage)
      : null;
  }

  removeMeFromParentArray(): void {
    const parentArray = this.form.parent;

    if (parentArray instanceof FormArray) {
      parentArray?.removeAt(parentArray?.length <= this.index ? parentArray?.length - 1 : this.index);
    }
    this.removeCalled = true;
  }

  ngOnDestroy(): void {
    if (isNaN(this.index)) {
      this.form && this.form.removeControl(this.formSectionName);
    } else if (!this.removeCalled) {
      this.removeMeFromParentArray();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Tries to find a control in the form, the parent form or by path provided as a string separated by comma
   * @param field
   * @private
   */
  private findFormControl(fieldIdentifier: string): AbstractControl | null {
    return (
      this.form.get(this.formSectionName)?.get(fieldIdentifier) ||
      this.form.get(fieldIdentifier) ||
      this.form.get(fieldIdentifier.split(','))
    );
  }
}
